import React from 'react'
import { Card, Heading, Text } from '../ui'

/**
 * Props interface for the AchievementsGrid component
 */
export interface AchievementsGridProps {
  /** Unique identifier for the section */
  id?: string
  /** CSS class name for custom styling */
  className?: string
}

/**
 * Achievement data matching the resume metrics
 */
const ACHIEVEMENTS_DATA = [
  {
    id: 'concurrent-users',
    number: '100,000+',
    label: 'Concurrent Users',
    description:
      'Architected platforms that scale to handle massive concurrent loads with zero downtime migrations and burst scalability.',
  },
  {
    id: 'cost-reduction',
    number: '40%',
    label: 'Cost Reduction',
    description:
      'Designed cost-optimization strategies that maintained flat infrastructure spend over 5+ years of sustained user growth.',
  },
  {
    id: 'developer-velocity',
    number: '4x',
    label: 'Developer Velocity',
    description:
      'Led integration of LLM-powered DevOps workflows that quadrupled developer throughput while improving release reliability.',
  },
  {
    id: 'shipped-games',
    number: '22',
    label: 'Shipped Games',
    description:
      "From DS cartridges to global mobile platforms, I've delivered systems that reach millions of users across 8 different platforms.",
  },
  {
    id: 'applications',
    number: '14',
    label: 'Applications Launched',
    description:
      'Built and deployed enterprise applications serving diverse industries from gaming to manufacturing to financial services.',
  },
  {
    id: 'platforms',
    number: '8',
    label: 'Platforms',
    description:
      'Cross-platform expertise spanning mobile, console, web, desktop, and embedded systems across multiple technology stacks.',
  },
] as const

/**
 * Achievements grid component showcasing key career accomplishments and metrics.
 *
 * Features:
 * - Quantified achievements with visual impact
 * - Responsive grid layout
 * - Accessible structure with proper ARIA labels
 * - Consistent with resume metrics
 * - Interactive hover states
 */
export const AchievementsGrid: React.FC<AchievementsGridProps> = ({
  id,
  className = '',
}) => {
  return (
    <section
      className={`about-section ${className}`}
      aria-labelledby="achievements-heading"
      id={id}
    >
      <Heading
        as="h2"
        size="xl"
        variant="default"
        className="section-title"
        id="achievements-heading"
      >
        What I'm Proud Of
      </Heading>

      <div
        className="achievements-grid"
        role="list"
        aria-label="Career achievements and metrics"
      >
        {ACHIEVEMENTS_DATA.map(achievement => (
          <Card
            key={achievement.id}
            variant="glass"
            padding="md"
            className="achievement-card"
            role="listitem"
          >
            <div className="achievement-metric">
              <Text
                size="xl"
                variant="accent"
                className="achievement-number"
                aria-label={`${achievement.number} ${achievement.label}`}
              >
                {achievement.number}
              </Text>
              <Text
                size="sm"
                variant="tertiary"
                id={`achievement-label-${achievement.id}`}
              >
                {achievement.label}
              </Text>
            </div>
            <Text
              size="sm"
              variant="secondary"
              aria-describedby={`achievement-label-${achievement.id}`}
            >
              {achievement.description}
            </Text>
          </Card>
        ))}
      </div>
    </section>
  )
}
